import { doc, setDoc, getDoc, increment, collection, query, orderBy, limit, getDocs, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Enhanced service for handling comprehensive visitor analytics
export class VisitorAnalyticsService {
  
  // Session management
  static sessionId = null;
  static sessionStartTime = null;
  static pageViewCount = 0;
  static lastActivityTime = null;
  
  // Initialize or get existing session
  static initializeSession() {
    if (!this.sessionId) {
      this.sessionId = this.generateSessionId();
      this.sessionStartTime = new Date();
      this.lastActivityTime = new Date();
      this.pageViewCount = 0;
    }
    return this.sessionId;
  }
  
  // Generate unique session ID
  static generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
  
  // Track a daily visitor with enhanced data
  static async trackDailyVisitor(additionalData = {}) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const sessionId = this.initializeSession();
      
      // Enhanced visitor data
      const visitorData = {
        timestamp: new Date().toISOString(),
        date: today,
        sessionId: sessionId,
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${screen.width}x${screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        colorDepth: screen.colorDepth,
        deviceMemory: navigator.deviceMemory || 'unknown',
        connectionType: navigator.connection?.effectiveType || 'unknown',
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        ...additionalData
      };

      // Update daily visitors count
      const visitorDocRef = doc(db, 'analytics', 'daily_visitors');
      const visitorDoc = await getDoc(visitorDocRef);

      if (visitorDoc.exists()) {
        const data = visitorDoc.data();
        await setDoc(visitorDocRef, {
          ...data,
          [today]: (data[today] || 0) + 1,
          lastUpdated: new Date().toISOString(),
          lastVisitor: visitorData
        }, { merge: true });
      } else {
        await setDoc(visitorDocRef, {
          [today]: 1,
          lastUpdated: new Date().toISOString(),
          lastVisitor: visitorData
        });
      }

      // Start session tracking
      await this.startSession(visitorData);

      console.log('Enhanced daily visitor tracked successfully for', today);
      return true;
    } catch (error) {
      console.error('Error tracking daily visitor:', error);
      return false;
    }
  }
  
  // Start user session tracking
  static async startSession(visitorData) {
    try {
      if (!this.sessionStartTime) {
        this.sessionStartTime = new Date();
      }
      
      const sessionData = {
        sessionId: this.sessionId,
        startTime: this.sessionStartTime.toISOString(),
        lastActivity: new Date().toISOString(),
        pageViews: 1,
        duration: 0,
        userAgent: visitorData?.userAgent || navigator.userAgent,
        referrer: visitorData?.referrer || document.referrer,
        language: visitorData?.language || navigator.language,
        timezone: visitorData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: visitorData?.screenResolution || `${screen.width}x${screen.height}`,
        viewportSize: visitorData?.viewportSize || `${window.innerWidth}x${window.innerHeight}`,
        platform: visitorData?.platform || navigator.platform,
        isActive: true,
        pages: [{
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
          title: document.title
        }]
      };
      
      const sessionsRef = collection(db, 'analytics', 'user_sessions', 'sessions');
      await setDoc(doc(sessionsRef, this.sessionId), sessionData);
      
      return true;
    } catch (error) {
      console.error('Error starting session:', error);
      return false;
    }
  }
  
  // Update session activity
  static async updateSession(pagePath) {
    try {
      if (!this.sessionId) return false;
      
      this.pageViewCount++;
      this.lastActivityTime = new Date();
      
      const duration = this.lastActivityTime - this.sessionStartTime;
      
      const sessionRef = doc(db, 'analytics', 'user_sessions', 'sessions', this.sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (sessionDoc.exists()) {
        const data = sessionDoc.data();
        const updatedPages = [...(data.pages || []), {
          path: pagePath,
          timestamp: new Date().toISOString(),
          title: document.title
        }];
        
        await setDoc(sessionRef, {
          lastActivity: this.lastActivityTime.toISOString(),
          pageViews: this.pageViewCount,
          duration: duration,
          pages: updatedPages,
          isActive: true
        }, { merge: true });
      }
      
      return true;
    } catch (error) {
      console.error('Error updating session:', error);
      return false;
    }
  }
  
  // End session
  static async endSession() {
    try {
      if (!this.sessionId) return false;
      
      const endTime = new Date();
      const duration = this.sessionStartTime ? (endTime - this.sessionStartTime) : 0;
      
      const sessionRef = doc(db, 'analytics', 'user_sessions', 'sessions', this.sessionId);
      await setDoc(sessionRef, {
        endTime: endTime.toISOString(),
        duration: duration,
        isActive: false
      }, { merge: true });
      
      // Reset session
      this.sessionId = null;
      this.sessionStartTime = null;
      this.pageViewCount = 0;
      this.lastActivityTime = null;
      
      return true;
    } catch (error) {
      console.error('Error ending session:', error);
      return false;
    }
  }

  // Get daily visitor statistics
  static async getDailyVisitorStats() {
    try {
      const visitorDocRef = doc(db, 'analytics', 'daily_visitors');
      const visitorDoc = await getDoc(visitorDocRef);

      if (visitorDoc.exists()) {
        return visitorDoc.data();
      } else {
        return {};
      }
    } catch (error) {
      console.error('Error getting daily visitor stats:', error);
      throw error;
    }
  }

  // Enhanced page view tracking with performance metrics
  static async trackPageView(pagePath, additionalData = {}) {
    try {
      const sessionId = this.initializeSession();
      
      // Performance metrics
      const performanceData = this.getPerformanceMetrics();
      
      const pageViewData = {
        path: pagePath,
        timestamp: new Date().toISOString(),
        date: new Date().toISOString().split('T')[0],
        sessionId: sessionId,
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        title: document.title,
        url: window.location.href,
        language: navigator.language,
        screenResolution: `${screen.width}x${screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        connectionType: navigator.connection?.effectiveType || 'unknown',
        deviceMemory: navigator.deviceMemory || 'unknown',
        ...performanceData,
        ...additionalData
      };

      // Add to page_views collection
      const pageViewsRef = collection(db, 'analytics', 'page_views', 'visits');
      await addDoc(pageViewsRef, pageViewData);
      
      // Update session
      await this.updateSession(pagePath);
      
      // Track scroll depth and engagement
      this.trackEngagementMetrics(pagePath);

      console.log('Enhanced page view tracked:', pagePath);
      return true;
    } catch (error) {
      console.error('Error tracking page view:', error);
      return false;
    }
  }
  
  // Get performance metrics
  static getPerformanceMetrics() {
    try {
      const navigation = performance.getEntriesByType('navigation')[0];
      const paint = performance.getEntriesByType('paint');
      
      return {
        loadTime: navigation ? Math.round(navigation.loadEventEnd - navigation.loadEventStart) : 0,
        domContentLoaded: navigation ? Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart) : 0,
        firstPaint: paint.find(p => p.name === 'first-paint')?.startTime || 0,
        firstContentfulPaint: paint.find(p => p.name === 'first-contentful-paint')?.startTime || 0,
        transferSize: navigation ? navigation.transferSize : 0,
        encodedBodySize: navigation ? navigation.encodedBodySize : 0,
        decodedBodySize: navigation ? navigation.decodedBodySize : 0
      };
    } catch (error) {
      console.warn('Could not get performance metrics:', error);
      return {};
    }
  }
  
  // Track user engagement metrics
  static trackEngagementMetrics(pagePath) {
    let scrollDepth = 0;
    let timeOnPage = Date.now();
    let clicks = 0;
    
    // Track scroll depth
    const trackScrollDepth = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentDepth = maxScroll > 0 ? Math.round((scrolled / maxScroll) * 100) : 0;
      scrollDepth = Math.max(scrollDepth, currentDepth);
    };
    
    // Track clicks
    const trackClicks = () => {
      clicks++;
    };
    
    window.addEventListener('scroll', trackScrollDepth, { passive: true });
    document.addEventListener('click', trackClicks);
    
    // Save engagement data on page unload
    const saveEngagementData = async () => {
      try {
        const engagementData = {
          path: pagePath,
          sessionId: this.sessionId,
          scrollDepth: scrollDepth,
          timeOnPage: Date.now() - timeOnPage,
          clicks: clicks,
          timestamp: new Date().toISOString()
        };
        
        const engagementRef = collection(db, 'analytics', 'engagement', 'metrics');
        await addDoc(engagementRef, engagementData);
      } catch (error) {
        console.warn('Could not save engagement data:', error);
      }
    };
    
    window.addEventListener('beforeunload', saveEngagementData);
    
    // Clean up after 30 seconds to avoid memory leaks
    setTimeout(() => {
      window.removeEventListener('scroll', trackScrollDepth);
      document.removeEventListener('click', trackClicks);
      window.removeEventListener('beforeunload', saveEngagementData);
    }, 30000);
  }

  // Track custom events (e.g., button clicks, form submissions)
  static async trackEvent(eventName, eventData = {}) {
    try {
      const eventRecord = {
        eventName: eventName,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        ...eventData
      };
      
      const eventsRef = collection(db, 'analytics', 'events', 'custom_events');
      await addDoc(eventsRef, eventRecord);
      
      console.log('Custom event tracked:', eventName);
      return true;
    } catch (error) {
      console.error('Error tracking custom event:', error);
      return false;
    }
  }
  
  // Track errors and exceptions
  static async trackError(error, context = {}) {
    try {
      const errorData = {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...context
      };
      
      const errorsRef = collection(db, 'analytics', 'errors', 'javascript_errors');
      await addDoc(errorsRef, errorData);
      
      console.log('Error tracked:', error.message);
      return true;
    } catch (err) {
      console.error('Error tracking error:', err);
      return false;
    }
  }
  
  // Track user interactions (clicks, form submissions, etc.)
  static async trackInteraction(interactionType, element, data = {}) {
    try {
      const interactionData = {
        type: interactionType,
        element: element,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        path: window.location.pathname,
        ...data
      };
      
      const interactionsRef = collection(db, 'analytics', 'interactions', 'user_interactions');
      await addDoc(interactionsRef, interactionData);
      
      return true;
    } catch (error) {
      console.error('Error tracking interaction:', error);
      return false;
    }
  }

  // Get visitor stats for a specific date range
  static getVisitorsForDateRange(visitorData, startDate, endDate) {
    if (!visitorData) return [];
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dateRange = [];
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dateStr = date.toISOString().split('T')[0];
      dateRange.push({
        date: dateStr,
        visitors: visitorData[dateStr] || 0,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })
      });
    }
    
    return dateRange;
  }

  // Get total visitors for all time
  static getTotalVisitors(visitorData) {
    if (!visitorData) return 0;
    return Object.keys(visitorData)
      .filter(key => key !== 'lastUpdated' && key !== 'lastVisitor')
      .reduce((total, date) => total + (visitorData[date] || 0), 0);
  }

  // Check if analytics collection exists and create it if needed
  static async initializeAnalytics() {
    try {
      const analyticsRef = doc(db, 'analytics', 'daily_visitors');
      const analyticsDoc = await getDoc(analyticsRef);
      
      if (!analyticsDoc.exists()) {
        const today = new Date().toISOString().split('T')[0];
        await setDoc(analyticsRef, {
          [today]: 0,
          lastUpdated: new Date().toISOString(),
          initialized: true
        });
        console.log('Analytics collection initialized');
      }
      
      // Setup automatic tracking
      this.setupVisibilityTracking();
      this.setupErrorTracking();
      
      return true;
    } catch (error) {
      console.error('Error initializing analytics:', error);
      return false;
    }
  }
  
  // Setup visibility tracking for page visits
  static setupVisibilityTracking() {
    try {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          // Page is hidden, end session if needed
          this.endSession();
        } else {
          // Page is visible, start tracking if needed
          this.trackPageView(window.location.pathname);
        }
      });
    } catch (error) {
      console.warn('Could not setup visibility tracking:', error);
    }
  }
  
  // Setup global error tracking
  static setupErrorTracking() {
    try {
      window.addEventListener('error', (event) => {
        this.trackError(event.error || new Error(event.message), {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        });
      });
      
      window.addEventListener('unhandledrejection', (event) => {
        this.trackError(new Error(event.reason), {
          type: 'unhandled_promise_rejection'
        });
      });
    } catch (error) {
      console.warn('Could not setup error tracking:', error);
    }
  }
  
  // Get comprehensive analytics summary
  static async getAnalyticsSummary(timeRange = '7days') {
    try {
      const [visitorStats, pageViews, sessions] = await Promise.all([
        this.getDailyVisitorStats(),
        this.getPageViewsInRange(timeRange),
        this.getSessionsInRange(timeRange)
      ]);
      
      return {
        visitorStats,
        pageViews,
        sessions,
        summary: {
          totalVisitors: this.getTotalVisitors(visitorStats),
          totalPageViews: pageViews.length,
          totalSessions: sessions.length,
          avgSessionDuration: this.calculateAvgSessionDuration(sessions),
          bounceRate: this.calculateBounceRate(sessions)
        }
      };
    } catch (error) {
      console.error('Error getting analytics summary:', error);
      throw error;
    }
  }
  
  // Helper method to get page views in time range
  static async getPageViewsInRange(timeRange) {
    try {
      const pageViewsRef = collection(db, 'analytics', 'page_views', 'visits');
      const q = query(pageViewsRef, orderBy('timestamp', 'desc'), limit(1000));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(view => this.isWithinTimeRange(view.timestamp, timeRange));
    } catch (error) {
      console.warn('Page views collection not found');
      return [];
    }
  }
  
  // Helper method to get sessions in time range
  static async getSessionsInRange(timeRange) {
    try {
      const sessionsRef = collection(db, 'analytics', 'user_sessions', 'sessions');
      const q = query(sessionsRef, orderBy('startTime', 'desc'), limit(500));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(session => this.isWithinTimeRange(session.startTime, timeRange));
    } catch (error) {
      console.warn('Sessions collection not found');
      return [];
    }
  }
  
  // Helper to check if timestamp is within time range
  static isWithinTimeRange(timestamp, timeRange) {
    if (!timestamp) return false;
    
    const date = new Date(timestamp);
    const now = new Date();
    const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    switch (timeRange) {
      case '1day': return daysDiff === 0;
      case '7days': return daysDiff <= 7;
      case '30days': return daysDiff <= 30;
      case '90days': return daysDiff <= 90;
      default: return daysDiff <= 7;
    }
  }
  
  // Calculate average session duration
  static calculateAvgSessionDuration(sessions) {
    if (sessions.length === 0) return 0;
    const totalDuration = sessions.reduce((sum, session) => sum + (session.duration || 0), 0);
    return Math.round(totalDuration / sessions.length / 1000); // Convert to seconds
  }
  
  // Calculate bounce rate
  static calculateBounceRate(sessions) {
    if (sessions.length === 0) return 0;
    const bouncedSessions = sessions.filter(session => session.pageViews === 1).length;
    return Math.round((bouncedSessions / sessions.length) * 100);
  }
}

export default VisitorAnalyticsService;